import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useStoreState } from "../util";
import axios from "axios";
import useSWR from "swr";
import { endpoints } from "../../endpoints";
import type {
  ListDetailsSchema,
  ListItemObjectSchema,
  ReferenceEntityConstraintSchema,
} from "../../types/types.PIM";

/**
 * Returns a debounced value (passed as props) after a delay (passed as props).
 *
 * Also returns a function that allows immediately setting the debounced value
 * that is returned (and clearing the existing timeout if one is in progress.)
 */
export function useDebounce<T>(
  value: T,
  delay: number
): [T, (value: T) => void] {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);
  const [timeoutId, setTimeoutId] = useState<number>();

  useEffect(
    () => {
      // Update debounced value after delay
      const id = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      setTimeoutId(id as unknown as number);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(id);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );

  const immediatelySetDebouncedValue = (value: T) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    setDebouncedValue(value);
  };

  return [debouncedValue, immediatelySetDebouncedValue];
}
/**
 * hook for tracking clicks outside of a container.
 * sourced from https://usehooks.com/useOnClickOutside/ which is public domain.
 * @param ref
 * @param handler
 */
export function useOnClickOutside(
  ref: React.RefObject<any>,
  handler: Function
) {
  useEffect(
    () => {
      const listener = (event: Event) => {
        // Do nothing if clicking ref's element or descendent elements
        if (!ref.current || ref.current.contains(event.target)) {
          return;
        }

        handler(event);
      };

      document.addEventListener("mousedown", listener);
      document.addEventListener("touchstart", listener);

      return () => {
        document.removeEventListener("mousedown", listener);
        document.removeEventListener("touchstart", listener);
      };
    },
    /*  
    Add ref and handler to effect dependencies
    It's worth noting that because passed in handler is a new 
    function on every render that will cause this effect
    callback/cleanup to run every render. It's not a big deal
    but to optimize you can wrap handler in useCallback before
    passing it into this hook. */
    [ref, handler]
  );
}

/**
 * Return an URLSearchParams object wrapping useLocation.search.
 * It is currently not possible to provide a generic interface to this, see
 * https://github.com/microsoft/TypeScript-DOM-lib-generator/issues/1202
 *
 * lifted from the react router docs here: https://v5.reactrouter.com/web/example/query-parameters
 */
export function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

/**
 * Hook for listening to global DOM events.
 * @param type The type of event to listen for.
 * @param eventHandler The event handler function.
 */
export function useGlobalDOMEvent<K extends keyof WindowEventMap>(
  type: K,
  eventHandler: (e: WindowEventMap[K]) => void
) {
  useEffect(() => {
    window.addEventListener<K>(type, eventHandler);

    return () => {
      window.removeEventListener(type, eventHandler);
    };
  }, [type, eventHandler]);
}

/**
 * Custom hook to initialize and fetch reference entity constraints for a collection.
 *
 * This hook returns an async function (`initializeConstraints`) that, given
 * a boolean indicating whether the current collection has reference entity mapping
 * enabled and the collection's ID, will fetch and return the list of constraints
 * for the collection from the backend API.
 *
 * It automatically pulls the necessary Storefront state/context required for the API call.
 *
 * Returns `undefined` if reference entities are disabled or not applicable.
 *
 * Usage:
 *   const initializeConstraints = useInitializeConstraints();
 *   const constraints = await initializeConstraints(hasReferenceEntityMapping, collectionId);
 *
 * @returns {Function} An async function to fetch constraints for the given collection.
 */

export type {
  UseDependentAttributesSingleOptions,
  UseDependentAttributesMultiOptions,
  UseDependentAttributesSingleResult,
  UseDependentAttributesMultiResult,
} from "./useDependentAttributes";
export { useDependentAttributes } from "./useDependentAttributes";
export { useDebouncedRowReorder } from "./useDebouncedRowReorder";

export function useInitializeConstraints() {
  const {
    storefront_id,
    storefront_metadata: { enable_reference_entities },
  } = useStoreState();
  const initializeConstraints = async (
    has_reference_entity_mapping: boolean,
    context_id: string,
    context_type: "group" | "collection"
  ) => {
    if (!enable_reference_entities || !has_reference_entity_mapping) {
      return;
    }
    const { data } = await axios.post<ReferenceEntityConstraintSchema>(
      endpoints.v2_storefronts_id_reference_entity_calculate_constraints(
        storefront_id
      ),
      {
        context_type,
        context_id,
        current_selections: [],
      }
    );
    return data.constraints;
  };
  return initializeConstraints;
}

export function useAssetHostData(tenantId: string) {
  const { data: assetsHostResponse } = useSWR<{ data: ListDetailsSchema }>(
    endpoints.v2_tenants_id_pim_get_list_by_name(tenantId, "Asset Host"),
    {
      onError: (error) => {
        console.error("There was an error fetching the Asset Host list.", {
          error,
        });
      },
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: true,
    }
  );

  const assetHostDefault = useMemo(() => {
    const agilisHost = assetsHostResponse?.data.items.find(
      (item: ListItemObjectSchema) => item.name === "Agilis"
    );
    return agilisHost
      ? { label: agilisHost.name, value: agilisHost.id }
      : undefined;
  }, [assetsHostResponse?.data.items]);

  return { assetsHostResponse, assetHostDefault };
}
